/**
 * LEGACY_DECOMMISSIONED (May 22, 2026)
 * Part of the legacy upload subsystem. Frozen. Do not extend.
 * Replacement: separate Video Analysis Engine (see VAE_CLEAN_ROOM_ARCHITECTURE.md).
 *
 * InstructorTrainingUploadModal — self-review upload for instructors.
 *
 * Feb 9, 2026 (P0 migration): the legacy single-file picker has been
 * replaced by the multi-clip basket flow. The modal is now a slim
 * two-step wrapper:
 *
 *   Step 1 — Pick the intent (Part 3 / Mentor / Reflection / etc.)
 *   Step 2 — Open the basket modal pre-wired with selfReview=true and
 *            the chosen intent; the user adds 1..N clips and the
 *            backend stitches them into ONE combined lesson_reviews
 *            doc with self_review=true.
 *
 * Single-clip = degenerate basket-of-one — no separate code path.
 *
 * The intent value is forwarded to /api/clip-basket/create so the
 * existing `lesson_reviews.intent` semantics + /my-training filter
 * continue to work.
 */
import { useState } from "react";
import {
  GraduationCap, X, BookOpen, Users, Brain, Target, Shield, Sparkles,
  ArrowRight,
} from "lucide-react";
import ClipBasketModal from "./ClipBasketModal";
import { MobileDialog } from "../mobile";
import { MEDIA_FEATURE_ENABLED } from "../../lib/mediaFeatureFlag";

// Owner-locked intent list — keep the wording tight and instructor-friendly.
const INTENTS = [
  {
    id: "part3_practice",
    label: "Part 3 practice",
    blurb: "Mock Part 3 lesson — graded against the 17 DVSA competencies.",
    Icon: GraduationCap,
  },
  {
    id: "mentor_review",
    label: "Mentor review",
    blurb: "Footage you want a mentor or trainer to comment on.",
    Icon: Users,
  },
  {
    id: "teaching_reflection",
    label: "Teaching reflection",
    blurb: "Self-reflection on your own teaching style and habits.",
    Icon: Brain,
  },
  {
    id: "client_centred",
    label: "Client-centred learning review",
    blurb: "Did the lesson stay learner-led? Where did it drift?",
    Icon: Target,
  },
  {
    id: "risk_management",
    label: "Risk management review",
    blurb: "Hazard identification, intervention timing, and decisions.",
    Icon: Shield,
  },
  {
    id: "general",
    label: "General self-review",
    blurb: "Anything else worth reviewing — calm, no pressure.",
    Icon: Sparkles,
  },
];

export default function InstructorTrainingUploadModal({ onClose, onUploaded, reviewIntent = null, resumeBasketId = null }) {
  const [intent, setIntent] = useState("part3_practice");
  // Phase B (Feb 13, 2026) — when the /start-review chooser routes
  // here with ?intent=<id>, skip the self-review intent picker and
  // jump straight into the basket. The chooser's selection is the
  // intent declaration; the modal's own list maps onto a sensible
  // self-review intent below.
  //
  // P0 May 20, 2026 — if we're recovering from an iOS Photos picker
  // handoff (resumeBasketId is set), also jump straight to basket.
  // The basket is already created server-side; we just need to
  // re-mount the modal pointing at it.
  const [step, setStep] = useState(reviewIntent || resumeBasketId ? "basket" : "intent");

  // Map the chooser's review_intent → the closest self-review
  // intent label so /my-training history continues to bucket
  // correctly. Keep this stable and explicit.
  const _selfReviewIntentFor = (ri) => {
    if (!ri) return intent;
    if (ri === "coaching_review") return "teaching_reflection";
    if (ri === "pdi_part3") return "part3_practice";
    if (ri === "mock_test") return "part3_practice";
    if (ri === "independent_drive") return "general";
    return "general";
  };

  // Media quarantine — silent. Modal hands off to ClipBasketModal →
  // /api/clip-basket/*, all 503 under quarantine.
  if (!MEDIA_FEATURE_ENABLED) return null;

  if (step === "basket") {
    // Hand off to the basket flow with selfReview + intent baked in.
    // The basket creates ONE combined lesson_reviews doc — no
    // separate per-clip review.
    return (
      <ClipBasketModal
        requestId={null}
        selfReview
        intent={_selfReviewIntentFor(reviewIntent) || intent}
        reviewIntent={reviewIntent}
        pupilName={null}
        focusArea={null}
        focusOther={null}
        timeRange={null}
        beaconSource="instructor_training_upload"
        resumeBasketId={resumeBasketId}
        onClose={onClose}
        onHandedOff={(basketId) => {
          // r14 May 22, 2026 — DO NOT navigate to /lesson-review
          // after a self-review upload. /lesson-review is the
          // learner review surface; instructor self-review records
          // live on the InstructorTrainingVideoCard via the
          // /api/lesson-review/my-training endpoint, which is on
          // the page we came FROM (instructor dashboard / PDI Hub
          // / Work Mode FEEDBACK). Navigating away here was the
          // exact bug the operator reported: "Upload reached 100%
          // but no result appeared" — because they were sent to
          // /lesson-review, which doesn't show training reviews.
          //
          // Behaviour now:
          //   1. Fire onUploaded → parent's fetchItems refreshes
          //      the training card → the new pending/analysing
          //      review appears immediately with a real review ID.
          //   2. Close the modal.
          //   3. Stay on the current page.
          onUploaded?.({ basket_id: basketId });
        }}
      />
    );
  }

  return (
    <MobileDialog
      bareContent
      open
      onClose={onClose}
      testid="self-review-upload-modal"
      priority="urgent"
    >
      <div
        className="w-full sm:max-w-md bg-slate-900 sm:rounded-2xl rounded-t-3xl border-t sm:border border-slate-700"
      >
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center">
              <GraduationCap className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest font-extrabold text-amber-300">
                Instructor development
              </p>
              <h3 className="text-base font-extrabold text-white leading-tight">
                Upload My Training Video
              </h3>
            </div>
          </div>
          <button
            data-testid="self-review-close"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5"
          >
            <X className="w-4 h-4 text-slate-400" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* Intent picker */}
          <div>
            <label className="block text-[10px] font-extrabold uppercase tracking-widest text-slate-500 mb-1.5">
              <BookOpen className="w-3 h-3 inline mr-1" /> What is this video for?
            </label>
            <div className="space-y-1.5">
              {INTENTS.map((it) => {
                const selected = intent === it.id;
                return (
                  <button
                    key={it.id}
                    type="button"
                    data-testid={`self-review-intent-${it.id}`}
                    onClick={() => setIntent(it.id)}
                    className={`w-full text-left rounded-lg p-3 ring-2 transition flex items-start gap-2.5 ${
                      selected
                        ? "ring-amber-500 bg-amber-500/15"
                        : "ring-transparent bg-slate-800 hover:bg-slate-700"
                    }`}
                  >
                    <it.Icon
                      className={`w-4 h-4 flex-none mt-0.5 ${
                        selected ? "text-amber-300" : "text-slate-400"
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-[13px] font-extrabold ${
                          selected ? "text-white" : "text-slate-200"
                        }`}
                      >
                        {it.label}
                      </p>
                      <p className="text-[11.5px] text-slate-400 leading-snug mt-0.5">
                        {it.blurb}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-500 mt-1.5">
              Saves to your CPD / Part 3 history. Never published to a learner.
            </p>
          </div>

          {/* Why basket — short explainer */}
          <div
            data-testid="self-review-basket-explainer"
            className="rounded-xl border border-indigo-700/40 bg-indigo-950/40 p-3"
          >
            <p className="text-[12px] text-indigo-100 leading-snug">
              <strong className="text-white">One lesson, one report.</strong>{" "}
              Add 1 clip for a quick self-review or stitch several short
              dashcam clips together. The AI treats them as ONE continuous
              session (lesson opening → middle → recap) and writes a single
              combined report.
            </p>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-800 flex gap-2 sticky bottom-0 bg-slate-900">
          <button
            data-testid="self-review-cancel"
            onClick={onClose}
            className="flex-1 h-12 rounded-xl bg-slate-800 text-slate-200 font-bold text-sm hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            data-testid="self-review-submit"
            onClick={() => setStep("basket")}
            className="flex-[2] h-12 rounded-xl bg-amber-500 hover:bg-amber-400 text-white font-extrabold text-sm transition flex items-center justify-center gap-2"
          >
            <ArrowRight className="w-4 h-4" /> Add clips
          </button>
        </div>
      </div>
    </MobileDialog>
  );
}
